import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints, colors } from '@/styles'
import CardDetails from './card-details'
import { CardHeader, CardContent, TextHeader } from './card-info'
import { TrustScoreSubmitted } from '../icons'

const Flex = styled.div`
  display: flex;
  gap: 0 24px;
  padding: 0 40px;
  ${props =>
    props.isSubmittedCard &&
    `
    align-items: center;
  `};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
    padding: 0 52px;
    > div:first-of-type {
      margin-bottom: 24px;
    }
  }
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0 20px;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${props =>
    !props.showDetails &&
    `
    > div {
      border-bottom: 1px dashed #d9d7d8;
    }
    > div:last-of-type {
      border-bottom: none;
    }
    > div:first-of-type {
      border-bottom: none;
    }
    #trust-docs {
      border: none;
      > div {
        border-bottom: 1px dashed #d9d7d8;
      }
      > div:last-of-type {
        border-bottom: ${!props.noDetails ? '1px dashed #d9d7d8' : 'none'};
      }
    }
  `};
`

const CardWrapper = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  height: 168px;
  width: 264px;
  flex: 264px 0 0;
  margin: 0 auto;
  border-radius: 16px;
  z-index: 1;
  background: ${colors.grey};
  background-image: url('${({ url }) => url}');
  background-size: cover;

  @media print {
    box-shadow: none;
    border: 1px solid ${colors.grey};
  }
`

const Link = styled.a`
  display: block;
  margin-top: 16px;
  color: ${colors.link};
  font-weight: 500;
`

const CardTemplate = props => {
  const {
    className,
    cardImage,
    title,
    creditRatingResult,
    creditDescription,
    creditDetails,
    userRole,
    professionId,
    csoVerifiedState,
    salarySort,
    listingData,
    showDetails,
    noDetails,
    user,
    secureFiles,
    children,
    unknownCredit
  } = props
  const { t } = useTranslation()
  const avatarUrl = user?.avatar?.aws_s3_key
  const printMedia = useMediaQuery('print')
  const isSubmittedCard = creditRatingResult === t('r.trust_score.completed.text', 'Completed')
  const isRenter = userRole === 'tenant'
  const isRequestSubmitted =
    creditRatingResult === t('r.trust_score.submitted.text', 'Submitted') && isRenter

  return (
    <Wrapper
      className={className}
      data-testid={props['data-testid']}
      showDetails={showDetails}
      noDetails={noDetails}
    >
      <Flex isSubmittedCard={isSubmittedCard}>
        {isSubmittedCard || isRequestSubmitted ? (
          <TrustScoreSubmitted />
        ) : (
          <CardWrapper
            url={cardImage}
            id={showDetails ? 'application-liv-score-close' : 'application-liv-score-open'}
          >
            <CardHeader user={user} score={creditDetails?.credit_rating} avatarUrl={avatarUrl} />
            <CardContent
              user={user}
              noDetails={noDetails}
              creditDetails={creditDetails}
              score={creditDetails?.credit_rating}
              rating={creditRatingResult}
            />
          </CardWrapper>
        )}
        <div style={isRenter ? { flex: 1 } : {}}>
          <TextHeader
            title={title}
            rating={creditRatingResult}
            score={creditDetails?.credit_rating}
            user={user}
            creditDescription={creditDescription}
            updatedAt={creditDetails?.updated_at}
            noDetails={noDetails}
            userRole={userRole}
          />
          {unknownCredit && (
            <Link href={`https://liv.rent/blog/livrent/trust-score-explained`} target="_blank">
              {t('l.application.view.learn_more.button', 'Learn more')}
            </Link>
          )}
        </div>
      </Flex>
      {!noDetails && (
        <CardDetails
          preview={!(showDetails || printMedia)}
          data={creditDetails}
          userRole={userRole}
          professionId={professionId}
          csoVerifiedState={csoVerifiedState}
          salarySort={salarySort}
          listingData={listingData}
          secureFiles={secureFiles}
        />
      )}
      {children}
    </Wrapper>
  )
}

export default CardTemplate
